import { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { ProgressBar } from "primereact/progressbar";
import { getAgregarUsuario, grabarUsuario } from "../../../../service/AffariService";
import { normalizarEmail, normalizarTelefono, validarEmail, validarTelefonoChileno, getPasswordStrength } from "../../../../utilities/validationUtils.js";
import { useGlobalContext } from "../../../../Config";

const canalOptions = [
    { label: 'SIN CANAL', value: 'SIN CANAL' },
    { label: 'MAYORISTA', value: 'MAYORISTA' },
    { label: 'RETAIL', value: 'RETAIL' },
];

const AgregarDialog = ({ visible, onSuccess, onCancel, toast }) => {
    const { isDinar, isSack } = useGlobalContext();
    const [loading, setLoading] = useState(false);
    const [codigo, setCodigo] = useState("");
    const [nombre, setNombre] = useState("");
    const [login, setLogin] = useState("");
    const [correo, setCorreo] = useState("");
    const [perfil, setPerfil] = useState(null);
    const [almacen, setAlmacen] = useState(null);
    const [perfiles, setPerfiles] = useState([]);
    const [almacenes, setAlmacenes] = useState([]);
    const [activo, setActivo] = useState(true);
    const [recibeEmail, setRecibeEmail] = useState(false);
    const [telefono, setTelefono] = useState("");
    const [canal, setCanal] = useState("");
    const [contraseña, setContraseña] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const passwordStrength = getPasswordStrength(contraseña);

    useEffect(() => {
        if (!visible ) {
            return;
        }
        setCodigo("");
        setNombre("");
        setLogin("");
        setCorreo("");
        setActivo(true); 
        setRecibeEmail(false);
        setTelefono("");
        setCanal("");
        setContraseña("");
        setPerfil(null);
        setAlmacen(null);
        setLoading(true);
        getAgregarUsuario()
            .then((data) => {
                if (!data) return;
                setPerfiles(Array.isArray(data.perfiles) ? data.perfiles : []);
                setAlmacenes(Array.isArray(data.almacenes) ? data.almacenes : []);
            })
            .catch((error) => {
                console.error("Error fetching usuario data:", error);
            })
            .finally(() => setLoading(false));
    }, [visible]);

    const handleGuardar = async () => {

        if (
            !codigo.trim() ||
            !nombre.trim() ||
            !login.trim() ||
            !contraseña.trim() ||
            !correo.trim() ||
            !perfil
        ) {
            if (toast?.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Validación",
                    detail: "Complete todos los campos obligatorios marcados con *",
                });
            }
            return;
        }


        const correoNormalizado = normalizarEmail(correo);
        const telefonoNormalizado = normalizarTelefono(telefono);

        if (!validarEmail(correoNormalizado)) {
            if (toast?.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Validación",
                    detail: "Ingrese un correo electrónico válido",
                });
            }
            return;
        }

        if (!validarTelefonoChileno(telefonoNormalizado)) {
            if (toast?.current) {
                toast.current.show({
                    severity: "warn",
                    summary: "Validación",
                    detail: "Ingrese un teléfono chileno válido",
                });
            }
            return;
        }

        const payload = {
            codigo: codigo || "",
            nombre: nombre || "",
            usuario: login || "",
            contrasena: contraseña || "",
            email: correoNormalizado || "",
            telefono: telefonoNormalizado || "",
            perfil: perfil?.id ?? null,
            almacen: almacen?.almacen ?? null,
            canal: canal || null,
            recibeEmail: recibeEmail ? 1 : 0,
            activo: activo ? 1 : 0,
        };

        try {
            setLoading(true);
            const response = await grabarUsuario(payload);
            if (toast?.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Éxito",
                    detail: response?.mensaje || "Usuario guardado correctamente",
                    life: 5000,
                });
            }
            onSuccess();
        } catch (error) {
            console.error("Error guardando usuario:", error);
            if (toast?.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: "No se pudo guardar el usuario",
                });
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            header={`Agregar Usuario`}
            visible={visible}
            className="p-fluid custom-dialog"
            modal
            onHide={onCancel}
            dismissableMask={true}
            footer={
                <div className="flex justify-content-end gap-2 pt-3 border-top-1 surface-border">
                    <Button label="Cancelar" icon="pi pi-times" onClick={onCancel} className="p-button-text" disabled={loading} />
                    <Button
                        label="Guardar"
                        icon={loading ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        onClick={handleGuardar}
                        disabled={loading}
                    />
                </div>
            }
        >
            <div className="formgrid grid p-fluid">
                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="codigo" className="font-medium mr-3 w-10rem flex-shrink-0">Código *</label>
                        <InputText
                            id="codigo"
                            value={codigo}
                            onChange={(e) => setCodigo(e.target.value)}
                            placeholder="Código del usuario"
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="nombre" className="font-medium mr-3 w-10rem flex-shrink-0">Nombre *</label>
                        <InputText
                            id="nombre"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                            placeholder="Nombre del usuario"
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="login" className="font-medium mr-3 w-10rem flex-shrink-0">Usuario *</label>
                        <InputText
                            id="login"
                            value={login}
                            onChange={(e) => setLogin(e.target.value)}
                            placeholder="Login"
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <div className="flex align-items-start mb-2">
                        <label htmlFor="constraseña" className="font-medium mr-3 w-10rem flex-shrink-0">Contraseña *</label>
                        <div className="w-full">
                            <div className="p-inputgroup">
                                <InputText
                                    id="constraseña"
                                    type={showPassword ? "text" : "password"}
                                    value={contraseña}
                                    onChange={(e) => setContraseña(e.target.value)}
                                    placeholder="contraseña"
                                    disabled={loading}
                                    className="w-full"
                                />
                                <Button
                                    type="button"
                                    icon={showPassword ? "pi pi-eye" : "pi pi-eye-slash"}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className="p-button-text p-button-secondary"
                                    disabled={loading}
                                />
                            </div>
                            <div className="mt-2">
                                <ProgressBar
                                    value={passwordStrength.value}
                                    showValue={false}
                                    className="border-round-sm"
                                    color={passwordStrength.color}
                                />
                                <small className="block mt-1">Seguridad: {passwordStrength.label}</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="correo" className="font-medium mr-3 w-10rem flex-shrink-0">Correo Electrónico *</label>
                        <InputText
                            id="correo"
                            value={correo}
                            onChange={(e) => setCorreo(e.target.value)}
                            placeholder="correo@ejemplo.cl"
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="telefono" className="font-medium mr-3 w-10rem flex-shrink-0">Teléfono</label>
                        <InputText
                            id="telefono"
                            value={telefono}
                            onChange={(e) => setTelefono(e.target.value)}
                            placeholder="telefono"
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>

                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="perfil" className="font-medium mr-3 w-10rem flex-shrink-0">Perfil *</label>
                        <Dropdown
                            id="perfil"
                            value={perfil}
                            options={perfiles}
                            onChange={(e) => setPerfil(e.value)}
                            optionLabel="descripcion"
                            placeholder="Seleccione perfil"
                            disabled={loading}
                            className="w-full"
                        />
                    </div>
                </div>

                {isSack && (
                    <>
                        <div className="field col-12">
                            <div className="flex align-items-center mb-2">
                                <label htmlFor="almacen" className="font-medium mr-3 w-10rem flex-shrink-0">Almacén</label>
                                <Dropdown
                                    id="almacen"
                                    value={almacen}
                                    options={almacenes}
                                    onChange={(e) => setAlmacen(e.value)}
                                    optionLabel="almacen"
                                    placeholder="Seleccione almacén"
                                    disabled={loading}
                                    className="w-full"
                                />
                            </div>
                        </div>
                        <div className="field col-12">
                            <div className="flex align-items-center mb-2">
                                <label htmlFor="canal" className="font-medium mr-3 w-10rem flex-shrink-0">Canal</label>
                                <Dropdown
                                    id="canal"
                                    value={canal}
                                    options={canalOptions}
                                    onChange={(e) => setCanal(e.value)}
                                    placeholder="Seleccione canal"
                                    disabled={loading}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </>
                )}

                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="recibeEmail" className="font-medium mr-3 w-10rem flex-shrink-0">Recibe E-mail</label>
                        <Checkbox
                            inputId="recibeEmail"
                            checked={recibeEmail}
                            onChange={(e) => setRecibeEmail(e.checked)}
                            disabled={loading}
                        />
                    </div>
                </div>
                <div className="field col-12">
                    <div className="flex align-items-center mb-2">
                        <label htmlFor="activo" className="font-medium mr-3 w-10rem flex-shrink-0">Activo</label>
                        <Checkbox
                            inputId="activo"
                            checked={activo}
                            onChange={(e) => setActivo(e.checked)}
                            disabled={loading}
                        />
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

export default AgregarDialog;
